/**
 * THE TRUST ROSTER: who is on a trust, in which capacity, from ONE source.
 *
 * ── Why this file exists (round 11, NEW-TRUST-OVERVIEW-REGISTER-CONTRADICTION)
 *
 * Seen live on two fresh trusts made by the setup commit: the overview's
 * Stakeholders card read "3 records" with no Founder, directly above a FICA card
 * reading "Nobody is on the trust register yet", while the People tab listed
 * FOUNDER 1 / TRUSTEES 2 / BENEFICIARY 1 for the same trust. Three readers, two
 * sources: Stakeholders read the beneficial ownership (BO) register and fell
 * back to the TRUSTEE list plus the beneficiary register when that was empty
 * (the setup commit writes no BO rows, so it always was), and the trustee list
 * has no founder in it; the People tab read the deed roster (`GET
 * /v1/trusts/{id}/members`) and the beneficiary register.
 *
 * A second source for one number is a bug generator (2026-08-12). Both screens
 * now call `buildTrustRoster` over the same two reads, so the bands, their
 * counts and the headcount phrase are the same values on both, by construction.
 *
 * The BO register is a DIFFERENT thing and is not read here: it is the register
 * of captured persons each FICA check is recorded against (the overview's FICA
 * card reads it). A person can be on the roster and not yet on that register.
 *
 * Everything below `buildTrustRoster` moved here verbatim from
 * `routes/app/trusts/$id/people-screen.tsx`; its history comments are kept.
 */

import type { BeneficiaryRow, TrustMemberView } from './api/trusts';
import { beneficiaryRelationshipLabel, deedRoleLabel } from './labels';
import { reorderSurnameFirst } from './format/person-name';

export type RosterRoleCategory = 'founder' | 'trustee' | 'beneficiary' | 'other';

/** Beno's walkthrough (2026-07-14 S16): "TRUSTEES 3 detected" counted a
 *  Founder (deed capacity "Skenker/Donor-Founder") as a trustee because he
 *  had been granted trustee-tier PORTAL ACCESS so he could act, but that
 *  access tier is not the same thing as the deed's actual trustee capacity.
 *  Categorise by the free-text `rosterRole` (the deed capacity) FIRST; only
 *  fall back to the portal access `tier` when the roster carries no capacity
 *  text at all (e.g. a pending invitee not yet matched to a roster row). A
 *  role naming BOTH capacities ("Settlor · Trustee") counts as a trustee:
 *  they genuinely are one, in addition to being a settlor. Exported for
 *  grouping the People screen + for unit testing. */
export function rosterRoleCategory(
  rosterRole: string | null,
  tier: 'trustee' | 'beneficiary' | null,
): RosterRoleCategory {
  const r = (rosterRole ?? '').trim().toLowerCase();
  if (r) {
    if (/trustee/.test(r)) return 'trustee';
    if (/founder|settlor|donor/.test(r)) return 'founder';
    if (/beneficiary/.test(r)) return 'beneficiary';
  }
  if (tier === 'trustee') return 'trustee';
  if (tier === 'beneficiary') return 'beneficiary';
  return 'other';
}

export const ROLE_GROUP_ORDER: RosterRoleCategory[] = ['founder', 'trustee', 'beneficiary', 'other'];

export const ROLE_GROUP_LABEL: Record<RosterRoleCategory, (count: number) => string> = {
  founder: (n) => (n === 1 ? 'Founder' : 'Founders'),
  trustee: (n) => (n === 1 ? 'Trustee' : 'Trustees'),
  beneficiary: (n) => (n === 1 ? 'Beneficiary' : 'Beneficiaries'),
  other: () => 'Other roles',
};

/** The three bands that ALWAYS draw (PPL-01). `other` stays zero-suppressed:
 *  it is a residual bucket for anything the deed calls something else, not one
 *  of the three capacities Beno asked to see. */
export const ALWAYS_SHOWN_BANDS: readonly RosterRoleCategory[] = ['founder', 'trustee', 'beneficiary'];

/**
 * One entry on the trust's LEGAL roster.
 *
 * Deliberately not a `TrustMemberView`. The beneficiary band also draws on the
 * trust's beneficiary register, and a roster entry has to be able to be an OPEN
 * CLASS ("the children born of the marriage") with no person behind it, no
 * login and no access tier. Beno's own example, and a shape the member list
 * cannot express.
 */
export interface RosterEntry {
  /** React key AND test id suffix. Band-qualified for a person's SECOND band
   *  (see `bandsForRosterRole`) so one person in two capacities never puts two
   *  identical ids in the DOM. */
  id: string;
  name: string;
  /** Deed capacity, already through `deedRoleLabel`. Null when none recorded. */
  capacity: string | null;
  /** True for a class of people rather than a named individual. */
  isOpenClass: boolean;
  /** The name to hand the cross-trust lookup, or null when there is nothing to
   *  look up (an unnamed roster row, or an open class, which is not a person). */
  crossTrustName: string | null;
}

/**
 * EVERY band a roster row belongs in, in deed order.
 *
 * A deed capacity can name more than one, and the literal Klaprops row does:
 * "Settlor & Trustee". Cornelis is the Founder AND a trustee, and a fiduciary
 * register lists him under both. Filing him under one only produced a screen
 * saying "no one on this trust's roster is recorded in a founding capacity"
 * three lines above a row reading "Founder & Trustee": a screen contradicting
 * itself in two adjacent places, which is the same defect as TRS-02 wearing a
 * different coat, and it appeared the moment the empty bands started drawing.
 *
 * `rosterRoleCategory` is NOT changed and stays the primary category: it is
 * what the residual bucket and the access-tier fallback key on, and its S16
 * rule (a Founder holding trustee-TIER ACCESS is not thereby a trustee) is
 * untouched, because that person's capacity text never says "trustee". This
 * only adds the further bands the same text also names.
 */
export function bandsForRosterRole(
  rosterRole: string | null,
  tier: 'trustee' | 'beneficiary' | null,
): RosterRoleCategory[] {
  const r = (rosterRole ?? '').trim().toLowerCase();
  const bands: RosterRoleCategory[] = [];
  if (r) {
    // Same alternations `rosterRoleCategory` uses, in ROLE_GROUP_ORDER order.
    if (/founder|settlor|donor/.test(r)) bands.push('founder');
    if (/trustee/.test(r)) bands.push('trustee');
    if (/beneficiary/.test(r)) bands.push('beneficiary');
  }
  return bands.length > 0 ? bands : [rosterRoleCategory(rosterRole, tier)];
}

/** Compare two free-text names for "these are the same person" when merging the
 *  member roster with the beneficiary register. Whitespace and case only, plus
 *  the SA identity card's "SURNAME, FORENAMES" order put back to reading order
 *  (the same reorder every display surface and the backend's account-holder
 *  match run; QA 4 Oct 2026). A cleverer match would start guessing, and the
 *  failure mode of guessing here is a person silently dropped off a statutory
 *  roster. A missing name (an old row without one) is never the same as
 *  anything, rather than a crash that takes the overview down. */
export function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (s: string | null | undefined) =>
    typeof s === 'string' ? reorderSurnameFirst(s).trim().toLowerCase().replace(/\s+/g, ' ') : '';
  return norm(a) === norm(b) && norm(a).length > 0;
}

/** Beneficiary-register row → roster entry. `relationship` is free text a firm
 *  typed and can carry the deed's own word, so it goes through the dictionary
 *  like every other capacity string on this screen. */
export function beneficiaryEntry(b: BeneficiaryRow): RosterEntry {
  const parts = [b.kind === 'class' ? 'Beneficiary class' : 'Beneficiary'];
  const relationship = beneficiaryRelationshipLabel(b.relationship);
  if (relationship) parts.push(relationship);
  if (b.lifeStatus === 'deceased') parts.push('deceased');
  return {
    id: `ben-${b.id}`,
    name: b.displayName,
    capacity: parts.join(' · '),
    isOpenClass: b.kind === 'class',
    crossTrustName: b.kind === 'class' ? null : b.displayName,
  };
}

/** The roster as both screens read it. */
export interface TrustRoster {
  /** Rows that came off the DEED (carry a `rosterId`). Orphan portal members
   *  and pending invitees are facts about access, not appointments, and are
   *  never printed on a statutory roster. */
  rosterMembers: TrustMemberView[];
  /** Every band, in `ROLE_GROUP_ORDER`. A person in two capacities appears in
   *  both bands. The beneficiary band is the roster's beneficiaries merged with
   *  the beneficiary register by name. */
  bands: Record<RosterRoleCategory, RosterEntry[]>;
  /** Whether the beneficiary register was read. While it is not, the
   *  beneficiary band holds only roster beneficiaries and no screen may report
   *  its count as a total. */
  beneficiaryRegisterKnown: boolean;
  /** Distinct NAMED people across the roster and the register: each roster row
   *  once, however many capacities it holds, plus register beneficiaries not
   *  already on the roster. An open class ("the children born of the
   *  marriage") is not a person and is not counted. */
  peopleCount: number;
}

/**
 * Build the trust roster from the two reads the People tab makes:
 * `useTrustMembers` (the deed roster joined to portal access) and
 * `useTrustBeneficiaries` (the beneficiary register). Pure.
 */
export function buildTrustRoster(
  members: readonly TrustMemberView[],
  beneficiaries: readonly BeneficiaryRow[] | undefined,
  opts: { beneficiaryRegisterKnown: boolean },
): TrustRoster {
  const rosterMembers = members.filter((m) => !!m.rosterId);

  const membersByRole: Record<RosterRoleCategory, TrustMemberView[]> = {
    founder: [], trustee: [], beneficiary: [], other: [],
  };
  for (const m of rosterMembers) {
    for (const cat of bandsForRosterRole(m.rosterRole, m.tier)) membersByRole[cat].push(m);
  }

  const rosterEntry = (m: TrustMemberView, cat: RosterRoleCategory): RosterEntry => {
    const base = m.userId ?? m.rosterId ?? m.displayName ?? 'unknown';
    // A person in two capacities renders in two bands, so the second one takes
    // a band-qualified id: two identical `data-testid`s in one document is a
    // trap for every test written after today.
    const primary = bandsForRosterRole(m.rosterRole, m.tier)[0]!;
    return {
      id: cat === primary ? base : `${cat}-${base}`,
      name: m.displayName ?? 'Unnamed on the deed roster',
      capacity: m.rosterRole ? deedRoleLabel(m.rosterRole) : null,
      isOpenClass: false,
      crossTrustName: m.displayName,
    };
  };

  // The beneficiary band merges the roster with the beneficiary register. The
  // merge is by NAME because those are the only two identifiers the two sources
  // share; a person on both must appear ONCE. (R-08, the same day: two cards for
  // one property. A second source for one fact is a duplicate generator unless
  // the merge is written down.)
  const rosterBeneficiaries = membersByRole.beneficiary.map((m) => rosterEntry(m, 'beneficiary'));
  const registerBeneficiaries = (beneficiaries ?? [])
    .filter((b) => b.status === 'active')
    .filter((b) => !rosterBeneficiaries.some((e) => sameName(e.name, b.displayName)))
    .map(beneficiaryEntry);

  const bands: Record<RosterRoleCategory, RosterEntry[]> = {
    founder: membersByRole.founder.map((m) => rosterEntry(m, 'founder')),
    trustee: membersByRole.trustee.map((m) => rosterEntry(m, 'trustee')),
    beneficiary: [...rosterBeneficiaries, ...registerBeneficiaries],
    other: membersByRole.other.map((m) => rosterEntry(m, 'other')),
  };

  // Distinct people: a register beneficiary who is ALSO on the roster in
  // another capacity (a trustee who is a beneficiary too) is one person, not
  // two. The band merge above only folds register rows into roster
  // BENEFICIARIES, which is right for the band and wrong for a headcount.
  const registerOnlyPeople = registerBeneficiaries.filter(
    (e) => !e.isOpenClass && !rosterMembers.some((m) => sameName(m.displayName ?? '', e.name)),
  );

  return {
    rosterMembers,
    bands,
    beneficiaryRegisterKnown: opts.beneficiaryRegisterKnown,
    peopleCount: rosterMembers.length + registerOnlyPeople.length,
  };
}

/** One person on the beneficial ownership (BO) register, as the overview's FICA
 *  card reads it. */
export interface RegisterPerson {
  id: string;
  fullName: string;
  category: string;
}

/**
 * The people the BO register holds who are NOT on the roster, one per person.
 *
 * QA 4 Oct 2026, P2-19. Round 11 made Stakeholders read the roster only, which
 * fixed one contradiction (a roster full of people above a FICA card saying
 * nobody was registered) and opened the opposite one. The per-document confirm
 * puts a deed's trustees in `trusteesPending`, NOT on the roster, while it does
 * write their BO rows; older manual capture did the same. On such a trust
 * Stakeholders said "No founder, trustee or beneficiary is recorded" (or "0
 * trustees") directly above a FICA card listing those trustees by name. Both
 * were true of different registers, and each panel read as a denial of the
 * other. The overview now names these people in Stakeholders too, as what they
 * are: captured on the BO register, not yet on the roster.
 *
 * Matched by NAME (`sameName`), because a roster row and a BO row share no id.
 * A person on the register twice (in two categories) is one person. Pure.
 */
export function registerOnlyPeople(
  roster: TrustRoster,
  register: readonly RegisterPerson[] | undefined,
): RegisterPerson[] {
  const onRoster = [
    ...roster.rosterMembers.map((m) => m.displayName ?? ''),
    ...ROLE_GROUP_ORDER.flatMap((cat) => roster.bands[cat].filter((e) => !e.isOpenClass).map((e) => e.name)),
  ];
  const out: RegisterPerson[] = [];
  for (const p of register ?? []) {
    if (typeof p.fullName !== 'string' || p.fullName.trim() === '') continue;
    if (onRoster.some((n) => sameName(n, p.fullName))) continue;
    if (out.some((q) => sameName(q.fullName, p.fullName))) continue;
    out.push(p);
  }
  return out;
}

/**
 * The roster headcount, as the People tab's Members band and the overview's
 * Stakeholders card both print it: "1 founder · 2 trustees · 1 beneficiary".
 * The beneficiary phrase is dropped while the register is unknown rather than
 * reported as zero.
 */
export function rosterCountPhrasesOf(roster: TrustRoster): string[] {
  const { bands } = roster;
  const phrases = [
    `${bands.founder.length} ${bands.founder.length === 1 ? 'founder' : 'founders'}`,
    `${bands.trustee.length} ${bands.trustee.length === 1 ? 'trustee' : 'trustees'}`,
  ];
  if (roster.beneficiaryRegisterKnown) {
    phrases.push(
      `${bands.beneficiary.length} ${bands.beneficiary.length === 1 ? 'beneficiary' : 'beneficiaries'}`,
    );
  }
  return phrases;
}
